// src-mobile/pages/Administration/ResetPassword.tsx
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../shared/api/axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../shared/contexts/AuthContext";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setToken, setUser } = useAuth();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pwChecks = useMemo(
    () => ({
      len: newPassword.length >= 8,
      digit: /\d/.test(newPassword),
    }),
    [newPassword]
  );

  const pwValid = pwChecks.len && pwChecks.digit;
  const pwMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = pwValid && pwMatch && !loading;

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      setError(
        t("resetPassword.requirements", {
          defaultValue:
            "Please meet the password requirements and make sure both fields match.",
        })
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      await api.post(`/auth/reset-password/${token}`, {
        newPassword,
      });

      setToken(null);
      setUser(null);

      localStorage.removeItem("unyToken");
      localStorage.removeItem("unyProfile");

      alert(t("resetPassword.success"));
      navigate("/", { replace: true });
    } catch (err: any) {
      console.error("❌ Reset password error:", err);
      const msg =
        err?.response?.data?.msg ||
        err?.message ||
        t("resetPassword.resetError");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageShell}>
      <header style={headerStyle}>
        <div style={logoWrap} onClick={() => navigate("/")}>
          <HighLogo
            src={logo}
            style={{ width: 100, display: "block" }}
            padding={0}
          />
        </div>

        <button onClick={() => navigate("/language")} style={languageBtn}>
          🌐 {t("language")}
        </button>
      </header>

      <main style={mainStyle}>
        <section style={resetPanel}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={backBtn}
          >
            ‹
          </button>

          <h1 style={titleStyle}>{t("resetPassword.title")}</h1>

          <p style={subtitleStyle}>
            {t("resetPassword.subtitle", {
              defaultValue: "Choose a new password for your account.",
            })}
          </p>

          <form onSubmit={handleResetPassword} style={formStyle}>
            <div style={fieldGroup}>
              <label htmlFor="newPassword" style={labelStyle}>
                {t("resetPassword.newPassword")}
              </label>

              <div style={inputWrap}>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder={t("resetPassword.placeholder", {
                    defaultValue: "At least 8 characters, incl. a number",
                  })}
                  style={inputWithIcon}
                />

                <div style={inputIcon}>
                  {newPassword.length > 0 &&
                    (pwValid ? (
                      <span style={{ color: "#22c55e" }}>✓</span>
                    ) : (
                      <span style={{ color: "#f97373" }}>✕</span>
                    ))}
                </div>
              </div>

              {newPassword.length > 0 && (
                <div style={passwordChecks}>
                  <span style={{ color: pwChecks.len ? "#22c55e" : "#f97373" }}>
                    {pwChecks.len ? "✓" : "✕"} 8+ characters
                  </span>
                  <span style={{ color: pwChecks.digit ? "#22c55e" : "#f97373" }}>
                    {pwChecks.digit ? "✓" : "✕"} at least 1 number
                  </span>
                </div>
              )}
            </div>

            <div style={fieldGroup}>
              <label htmlFor="confirmPassword" style={labelStyle}>
                {t("resetPassword.confirmNewPassword")}
              </label>

              <div style={inputWrap}>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder={t("resetPassword.confirmPlaceholder", {
                    defaultValue: "Repeat new password",
                  })}
                  style={inputWithIcon}
                />

                <div style={inputIcon}>
                  {confirmPassword.length > 0 &&
                    (pwMatch ? (
                      <span style={{ color: "#22c55e" }}>✓</span>
                    ) : (
                      <span style={{ color: "#f97373" }}>✕</span>
                    ))}
                </div>
              </div>

              {confirmPassword.length > 0 && (
                <div style={statusLine}>
                  <span style={{ color: pwMatch ? "#22c55e" : "#f97373" }}>
                    {pwMatch ? "✓" : "✕"} passwords match
                  </span>
                </div>
              )}
            </div>

            {error && <p style={errorStyle}>{error}</p>}

            <button
              type="submit"
              disabled={!canSubmit}
              style={canSubmit ? submitBtn : submitBtnDisabled}
            >
              {loading
                ? t("resetPassword.resetting", { defaultValue: "Resetting…" })
                : t("resetPassword.resetButton")}
            </button>
          </form>

          <div style={legalBlock}>
            <div style={{ marginBottom: 8 }}>
              {t("legalNoticeforgotpassword", {
                defaultValue: "By continuing, you agree to our policies.",
              })}
            </div>

            <span
              onClick={() => navigate("/legal/privacypolicy")}
              style={legalLink}
            >
              {t("profileSetup.privacyPolicy", "Privacy Policy")}
            </span>

            {" · "}

            <span
              onClick={() => navigate("/legal/termsofuse")}
              style={legalLink}
            >
              {t("profileSetup.termsOfUse", "Terms of Use")}
            </span>
          </div>
        </section>
      </main>
    </div>
  );
};

const pageShell: React.CSSProperties = {
  minHeight: "100vh",
  width: "100%",
  background:
    "radial-gradient(circle at top, #111827 0, #020617 45%, #000 100%)",
  color: "#f9fafb",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: "flex",
  flexDirection: "column",
};

const headerStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  background: "rgba(2, 6, 23, 0.86)",
  backdropFilter: "blur(12px)",
  borderBottom: "1px solid rgba(148,163,184,0.16)",
};

const logoWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  marginLeft: "-20px",
};

const languageBtn: React.CSSProperties = {
  background: "rgba(15,23,42,0.75)",
  border: "1px solid rgba(148,163,184,0.35)",
  borderRadius: 999,
  padding: "6px 14px",
  cursor: "pointer",
  fontSize: 14,
  color: "#e5e7eb",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 14px calc(8dvh + env(safe-area-inset-bottom))",
};

const resetPanel: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 430,
  borderRadius: 28,
  border: "1px solid rgba(148,163,184,0.24)",
  background:
    "linear-gradient(145deg, rgba(15,23,42,0.94), rgba(2,6,23,0.98))",
  boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
  padding: "34px 24px 32px",
};

const backBtn: React.CSSProperties = {
  position: "absolute",
  top: 4,
  left: -2,
  width: 42,
  height: 42,
  borderRadius: "50%",
  background: "transparent",
  border: "none",
  color: "#c2ccd8",
  cursor: "pointer",
  fontSize: 30,
  lineHeight: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const titleStyle: React.CSSProperties = {
  fontSize: 30,
  lineHeight: 1.05,
  margin: "0 0 12px 0",
  color: "#f8fafc",
  letterSpacing: -0.8,
  textAlign: "center",
};

const subtitleStyle: React.CSSProperties = {
  margin: "0 auto 24px",
  color: "#94a3b8",
  fontSize: 13,
  lineHeight: 1.5,
  maxWidth: 330,
  textAlign: "center",
};

const formStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const fieldGroup: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#cbd5e1",
  fontWeight: 600,
  marginLeft: 5,
};

const inputWrap: React.CSSProperties = {
  position: "relative",
};

const inputModern: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(2,6,23,0.85)",
  color: "#f8fafc",
  fontSize: 16,
  outline: "none",
  boxSizing: "border-box",
};

const inputWithIcon: React.CSSProperties = {
  ...inputModern,
  paddingRight: 40,
};

const inputIcon: React.CSSProperties = {
  position: "absolute",
  right: 14,
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: 16,
  pointerEvents: "none",
};

const passwordChecks: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  fontSize: 11,
  marginTop: 2,
};

const statusLine: React.CSSProperties = {
  minHeight: 15,
  fontSize: 11,
  marginTop: 2,
};

const errorStyle: React.CSSProperties = {
  color: "#fca5a5",
  margin: "2px 0 0",
  textAlign: "center",
  fontSize: 13,
};

const submitBtn: React.CSSProperties = {
  marginTop: 8,
  padding: "12px 18px",
  borderRadius: 999,
  border: "1px solid rgba(250,250,250,0.22)",
  background:
    "linear-gradient(135deg, rgba(212,175,55,0.98), rgba(250,250,210,0.92))",
  color: "#111827",
  fontWeight: 800,
  fontSize: 15,
  cursor: "pointer",
};

const submitBtnDisabled: React.CSSProperties = {
  ...submitBtn,
  background: "rgba(75,85,99,0.65)",
  color: "#cbd5e1",
  cursor: "not-allowed",
};

const legalBlock: React.CSSProperties = {
  marginTop: 18,
  textAlign: "center",
  fontSize: 10.5,
  color: "#64748b",
  lineHeight: 1.5,
};

const legalLink: React.CSSProperties = {
  cursor: "pointer",
  color: "#94a3b8",
};

export default ResetPassword;